import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { FaArrowLeft, FaArrowRight, FaTimes } from 'react-icons/fa';
import Spinner from '../components/Spinner';
import { toast } from 'react-toastify';

export default function FullImagePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [listing, setListing] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isZoomed, setIsZoomed] = useState(false);
  const imageRefs = useRef([]);
  const scrollContainerRef = useRef(null);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Fetch listing data
  useEffect(() => {
    const fetchListing = async () => {
      try {
        const listingRef = doc(db, 'listings', id);
        const listingSnap = await getDoc(listingRef);
        if (listingSnap.exists()) {
          setListing({ id: listingSnap.id, ...listingSnap.data() });
        } else {
          toast.error('Listing not found');
          navigate('/categories');
        }
      } catch (error) {
        console.error('Error fetching listing:', error);
        toast.error('Failed to load listing');
        navigate('/categories');
      } finally {
        setLoading(false);
      }
    };

    fetchListing();
  }, [id, navigate]);

  // Handle image navigation
  const goToPrevious = () => {
    const newIndex = currentIndex === 0 ? listing.imageUrls.length - 1 : currentIndex - 1;
    setCurrentIndex(newIndex);
    scrollToImage(newIndex);
  };

  const goToNext = () => {
    const newIndex = currentIndex === listing.imageUrls.length - 1 ? 0 : currentIndex + 1;
    setCurrentIndex(newIndex);
    scrollToImage(newIndex);
  };

  // Scroll to specific image
  const scrollToImage = (index) => {
    if (scrollContainerRef.current && imageRefs.current[index]) {
      imageRefs.current[index].scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center'
      });
    }
  };

  // Handle zoom
  const handleZoom = (index) => {
    setIsZoomed(!isZoomed);
    if (imageRefs.current[index]) {
      imageRefs.current[index].style.transform = isZoomed ? 'scale(1)' : 'scale(2)';
      imageRefs.current[index].style.transformOrigin = 'center';
    }
  };

  // Handle close
  const handleClose = () => {
    navigate(`/listing/${id}`);
  };

  // Touch event handlers for mobile swipe
  const handleTouchStart = (e) => {
    if (isZoomed) return;
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    if (isZoomed) return;
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (isZoomed) return;
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) goToNext();
    if (isRightSwipe) goToPrevious();
    
    setTouchStart(null);
    setTouchEnd(null);
  };

  // Update current index when scroll ends
  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const scrollPosition = container.scrollLeft;
      const containerWidth = container.clientWidth;
      const newIndex = Math.round(scrollPosition / containerWidth);
      
      if (newIndex !== currentIndex) {
        setCurrentIndex(newIndex);
      }
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') goToPrevious();
      if (e.key === 'ArrowRight') goToNext();
      if (e.key === 'Escape') handleClose();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, listing]);

  if (loading) {
    return <Spinner />;
  }

  if (!listing || !listing.imageUrls || listing.imageUrls.length === 0) {
    return (
      <div className="flex items-center justify-center h-screen bg-black text-white">
        No images available
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black z-50 flex items-center justify-center p-4">
      {/* Close Button */}
      <button
        onClick={handleClose}
        className="absolute top-4 right-4 text-white text-2xl hover:text-[#4da3ff] transition-colors duration-200 z-50"
        aria-label="Close full image view"
      >
        <FaTimes />
      </button>

      {/* Image Container */}
      <div className="relative w-full max-w-4xl h-[90vh]">
        {/* Navigation Arrows */}
        <button
          onClick={goToPrevious}
          className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white text-3xl hover:text-[#4da3ff] transition-colors duration-200 disabled:opacity-50 z-10"
          disabled={listing.imageUrls.length <= 1}
          aria-label="Previous image"
        >
          <FaArrowLeft />
        </button>
        <button
          onClick={goToNext}
          className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white text-3xl hover:text-[#4da3ff] transition-colors duration-200 disabled:opacity-50 z-10"
          disabled={listing.imageUrls.length <= 1}
          aria-label="Next image"
        >
          <FaArrowRight />
        </button>

        {/* Scrollable Images Container */}
        <div 
          ref={scrollContainerRef}
          className="w-full h-full flex overflow-x-auto snap-x snap-mandatory scrollbar-hide"
          style={{
            scrollSnapType: 'x mandatory',
            WebkitOverflowScrolling: 'touch',
          }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onScroll={handleScroll}
        >
          {listing.imageUrls.map((url, index) => (
            <div 
              key={index}
              className="flex-shrink-0 w-full h-full flex items-center justify-center snap-center relative"
              style={{ scrollSnapAlign: 'center' }}
            >
              <img
                ref={(el) => (imageRefs.current[index] = el)}
                src={url}
                alt={`Listing image ${index + 1}`}
                className="max-h-full max-w-full object-contain cursor-pointer transition-transform duration-300"
                onClick={() => handleZoom(index)}
                onError={(e) => (e.target.src = '/default-image.jpg')}
                style={{
                  transform: isZoomed && currentIndex === index ? 'scale(2)' : 'scale(1)',
                  transformOrigin: 'center',
                }}
              />
            </div>
          ))}
        </div>

        {/* Image Counter */}
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-white text-black px-3 py-1 rounded-full text-sm z-10">
          {currentIndex + 1} / {listing.imageUrls.length}
        </div>
      </div>
    </div>
  );
}